import DownloadIcon from "@assets/images/down-arrow.svg";
import {
  Card,
  DialogContentText,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ActionDialog from "@saleor/components/ActionDialog";
import ButtonMultiAction from "@saleor/components/ButtonMultiAction";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Hr from "@saleor/components/Hr";
import PageHeader from "@saleor/components/PageHeader";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import useDownloadReport from "@saleor/hooks/useDownloadReport";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useUser from "@saleor/hooks/useUser";
import { maybe, renderCollection } from "@saleor/misc";
import { createReportStructure } from "@saleor/reports/batch/utils/reportsDownload";
import { reportsDownload } from "@saleor/reports/utils/utils";
import { SmopUserTypeCode } from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  useBatchStatusBulkApprove,
  useBatchStatusBulkCancel,
  useBatchStatusReject
} from "../../mutation";
import {
  useActiveBatchDetailsReportQuery,
  useBatchDetailsQuery,
  usePayablesListQuery
} from "../../queries";
import { useActiveBatchDetailsReportQuery as useActivebatchReport } from "../../queries";
import { batchDetailsQuery_batchDetails_data } from "../../types/batchDetailsQuery";
import {
  batchDetailsUrl,
  batchListPath,
  BatchListUrlDialog,
  BatchListUrlQueryParams
} from "../../urls";
import {
  batchDetailsDownload,
  formatDate,
  getYear,
  payablesListDownload
} from "../../utils/handlers";
import ActionButtons from "../ActionButtons";
import CancelBatchDialog from "../CancelBatchDialog";
import ConfirmDialog from "../ConfirmDialog";
import PermissionSection from "../PermissionSection";
import RejectBatchDialog from "../RejectBatchDialog";
import BackButton from "./components/BackButton";

const useStyles = makeStyles(
  theme => ({
    card: {
      marginTop: "20px",
      padding: "20px"
    },
    id: {
      display: "flex",
      justifyContent: "space-between",
      marginLeft: "23px",
      marginTop: "4px",
      width: "400px"
    },
    pageActions: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: theme.spacing(3),
      paddingRight: 0
    },
    specialistActionBtn: {
      "& :nth-child(2)": {
        marginRight: "10px"
      },
      display: "flex",
      flexDirection: "row-reverse",
      justifyContent: "space-between"
    },
    title: {
      alignItems: "end"
    },
    titleSkeleton: {
      display: "flex",
      height: "1em",
      justifyContent: "space-between",
      marginLeft: "23px",
      width: "400px"
    }
  }),
  { name: "PageTitle" }
);

function BatchDetails(props) {
  const [downloadStatus, setDownloadStatus] = useState<
    ConfirmButtonTransitionState
  >("default");
  const [loadReportData, setLoadReportData] = useState<boolean>(false);
  const [buttonState, setButtonState] = useState<ConfirmButtonTransitionState>(
    "default"
  );

  const { id, params } = props;
  const intl = useIntl();
  const classes = useStyles(props);
  const navigate = useNavigator();
  const notify = useNotifier();
  const { user } = useUser();

  const { data, loading } = useBatchDetailsQuery({
    displayLoader: true,
    skip: false,
    variables: {
      id: btoa(id)
    }
  });

  const {
    data: reportData,
    loading: reportLoading
  } = useActiveBatchDetailsReportQuery({
    skip: !loadReportData || params.action !== "download-active-batch",
    variables: {
      ids: [btoa(id)]
    }
  });

  const { data: batchReport } = useActivebatchReport({
    variables: {
      ids: [btoa(id)]
    }
  });

  const { data: payablesList } = usePayablesListQuery({
    displayLoader: true,
    skip: user.userType.code !== SmopUserTypeCode.FINANCE_MANAGER,
    variables: {
      ids: [btoa(id)]
    }
  });

  const batchDetails = data?.batchDetails?.data?.[0];
  const batchIdFormat = `${batchDetails?.batch_id}`;

  const [batchStatusReject] = useBatchStatusReject({
    onCompleted: data => {
      const { batchStatusReject } = data;
      if (batchStatusReject.batchErrors.length === 0) {
        notify({
          status: "success",
          text: `${batchIdFormat} has been rejected and returned to Open Orders.`
        });
        navigate("/payout/batch-history");
      } else {
        notify({
          status: "error",
          text: "The server encountered an error processing the request"
        });
      }
    }
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    BatchListUrlDialog,
    BatchListUrlQueryParams
  >(navigate, params => batchDetailsUrl(id, params), params);

  const [batchStatusApprove] = useBatchStatusBulkApprove({
    onCompleted: event => {
      const { batchErrors } = event?.batchStatusBulkApprove;

      if (batchErrors?.length !== 0) {
        notify({
          status: "error",
          text: batchErrors[0].message
        });
      } else {
        notify({
          status: "success",
          text: `You have successfully approved Batch ID ${batchIdFormat}`
        });

        setButtonState("success");
        closeModal();
        navigate(batchListPath);
      }
    }
  });

  const [BatchStatusBulkCancel] = useBatchStatusBulkCancel({
    onCompleted: event => {
      const { batchStatusBulkCancel } = event;
      const { batchErrors, count } = batchStatusBulkCancel;

      setButtonState("default");

      if (batchErrors.length !== 0) {
        notify({
          status: "error",
          text: batchErrors[0].message
        });
      } else {
        if (count === 1) {
          notify({
            status: "success",
            text: `${batchIdFormat} Active Batch has been approved`
          });
          closeModal();
          navigate(batchListPath);
        }
      }
    }
  });

  useEffect(() => {
    if (!loadReportData || reportLoading) {
      return;
    }

    setDownloadStatus("loading");

    const reportStructure = createReportStructure(
      reportData?.activeBatchDetailsReport?.edges?.map(data => data?.node),
      `${batchDetails?.mall_code}_${getYear(batchDetails?.date_generated)}_${
        batchDetails?.batch_id
      }`
    );

    useDownloadReport(
      reportStructure.data,
      reportStructure.fields,
      reportStructure.sheetname,
      reportStructure.filename,
      "csv"
    );

    notify({
      status: "success",
      text: "Successfully downloaded the report"
    });

    closeModal();
    setDownloadStatus("success");
    setLoadReportData(false);
  }, [loadReportData, reportLoading]);

  const arr = batchReport?.activeBatchDetailsReport.edges.map(a => a.node);

  const handleMngrDownload = async () => {
    await batchDetailsDownload(arr).finally(() => {
      notify({
        status: "success",
        text: "Successfully downloaded the report"
      });
      closeModal();
    });
  };

  const payablesListReport = payablesList?.payablesList.edges.map(a => a.node);

  const onGeneratePayableList = () => {
    const data = JSON.parse(JSON.stringify(payablesListReport));
    payablesListDownload(
      data,
      "batch_id",
      reportsDownload.payablesListPayouts.fields
    ).finally(() => {
      notify({
        status: "success",
        text: "Successfully downloaded the report"
      });
      closeModal();
    });
  };

  return (
    <>
      <Container>
        <Container className={classes.pageActions}>
          <BackButton />
          <PermissionSection
            userTypes={[
              SmopUserTypeCode.FINANCE_SPECIALIST,
              SmopUserTypeCode.FINANCE_MANAGER
            ]}
          >
            <ActionButtons
              className={classes.specialistActionBtn}
              action={{
                negativeBtn: {
                  label: "Cancel",
                  onClick: () => openModal("cancel")
                },
                positiveBtn: {
                  icon: {
                    src: DownloadIcon,
                    type: "img"
                  },
                  onClick: () => openModal("download-active-batch"),
                  type: "icon"
                }
              }}
            />
            <ButtonMultiAction
              actions={[
                {
                  action: () => openModal("FnManagerdowloadActiveDetails"),
                  label: "Download Batch"
                },
                {
                  action: () => openModal("FnManagerGeneratePayableList"),
                  label: "Generate Payable List"
                }
              ]}
              label="Download"
            />
          </PermissionSection>
        </Container>
        <Card className={classes.card}>
          <PageHeader
            className={classes.title}
            title={
              loading ? (
                <Skeleton className={classes.titleSkeleton} />
              ) : (
                <label className={classes.id}>{batchIdFormat}</label>
              )
            }
          >
            <PermissionSection
              userTypes={[
                SmopUserTypeCode.FINANCE_SPECIALIST,
                SmopUserTypeCode.FINANCE_MANAGER
              ]}
            >
              <></>
              <ActionButtons
                action={{
                  negativeBtn: {
                    label: "Reject",
                    onClick: () => openModal("reject")
                  },
                  positiveBtn: {
                    label: "Approve",
                    onClick: () => openModal("approve")
                  }
                }}
              />
            </PermissionSection>
          </PageHeader>
          <Hr></Hr>
          <ResponsiveTable>
            <TableHead>
              <TableCellHeader>
                <FormattedMessage defaultMessage="Mall" />
              </TableCellHeader>
              <TableCellHeader textAlign="center">
                <FormattedMessage defaultMessage="Brand" />
              </TableCellHeader>
              <TableCellHeader textAlign="center">
                <FormattedMessage defaultMessage="Company Name" />
              </TableCellHeader>
              <TableCellHeader textAlign="right">
                <FormattedMessage defaultMessage="Period Covered" />
              </TableCellHeader>
              <TableCellHeader textAlign="right">
                <FormattedMessage defaultMessage="Net Settlement" />
              </TableCellHeader>
              <TableCellHeader textAlign="right">
                <FormattedMessage defaultMessage="Settlement Date" />
              </TableCellHeader>
            </TableHead>
            <TableBody>
              {data?.batchDetails?.data &&
                renderCollection(
                  data?.batchDetails?.data,
                  (batchDetail: batchDetailsQuery_batchDetails_data) => (
                    <TableRow>
                      <TableCell>
                        {maybe(() => batchDetail.mall_name, null) ? (
                          batchDetail.mall_name.toUpperCase()
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {maybe(() => batchDetail.brand_name, null) ? (
                          batchDetail.brand_name.toUpperCase()
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {maybe(() => batchDetail.company_name, null) ? (
                          batchDetail.company_name.toUpperCase()
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {maybe(
                          () =>
                            batchDetail.coverage_start_date &&
                            batchDetail.coverage_end_date,
                          null
                        ) ? (
                          <Typography>
                            {formatDate(batchDetail.coverage_start_date)}-
                            {formatDate(batchDetail.coverage_end_date)}
                          </Typography>
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {maybe(() => batchDetail.net_settlement, null) ? (
                          batchDetail.net_settlement
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {maybe(() => batchDetail.settlement_date, null) ? (
                          formatDate(batchDetail.settlement_date)
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                    </TableRow>
                  ),
                  () => (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <FormattedMessage defaultMessage="No details found" />
                      </TableCell>
                    </TableRow>
                  )
                )}
            </TableBody>
          </ResponsiveTable>
        </Card>
      </Container>

      <ConfirmDialog
        buttonState={downloadStatus}
        content={
          <FormattedMessage
            defaultMessage="You are about to download Active Batch with {total} row{s}. Do you want to continue?"
            values={{
              s: data?.batchDetails?.data?.length > 1 ? "s" : "",
              total: data?.batchDetails?.data?.length
            }}
          />
        }
        onClose={() => {
          closeModal();
          setDownloadStatus("default");
        }}
        onConfirm={() => setLoadReportData(true)}
        open={params.action === "download-active-batch"}
        title="Download Active Batch"
      />

      <ConfirmDialog
        buttonState={buttonState}
        closeButtonLabel="back"
        content={
          <FormattedMessage defaultMessage="Are you sure you wanted to Approve?" />
        }
        contentAlignment="center"
        fullWidthButtons={true}
        onClose={closeModal}
        onConfirm={() => {
          setButtonState("loading");
          batchStatusApprove({
            variables: {
              ids: [btoa(id)]
            }
          });
        }}
        open={params.action === "approve"}
      />

      <RejectBatchDialog
        open={params.action === "reject"}
        onClose={closeModal}
        onClickReject={remarks => {
          batchStatusReject({
            variables: {
              id: btoa(id),
              remarks
            }
          });
          closeModal();
        }}
      />

      <CancelBatchDialog
        open={params.action === "cancel"}
        onClose={closeModal}
        listElements={["a"]}
        loading={buttonState === "loading"}
        onConfirm={() => {
          BatchStatusBulkCancel({
            variables: {
              ids: [btoa(id)]
            }
          });
          setButtonState("loading");
        }}
        cancelButtonLabel="Back"
        confirmButtonLabel="Yes"
      />
      <ActionDialog
        open={params.action === "FnManagerdowloadActiveDetails"}
        onClose={closeModal}
        confirmButtonState="default"
        onConfirm={handleMngrDownload}
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Download Batch",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="You are about to download batch with {total} row{s}. Do you want to continue?"
            values={{
              s: arr?.length > 1 ? "s" : "",
              total: arr?.length
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <ActionDialog
        open={params.action === "FnManagerGeneratePayableList"}
        onClose={closeModal}
        confirmButtonState="default"
        onConfirm={onGeneratePayableList}
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Generate Payable List",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="You are about to download Payable List with {total} row{s}. Do you want to continue?"
            values={{
              s: payablesList?.payablesList?.totalCount > 1 ? "s" : "",
              total: payablesList?.payablesList?.totalCount
            }}
          />
        </DialogContentText>
      </ActionDialog>
    </>
  );
}

export default BatchDetails;
